import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ShieldCheck, Boxes, Network, Activity } from "lucide-react";

function Number({ to, prefix = "", suffix = "", decimals = 0, duration = 2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(eased * to);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return (
    <span ref={ref} className="ticker-digit">
      {prefix}
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
}

const stats = [
  {
    icon: ShieldCheck,
    label: "Assets under custody",
    val: 2.4,
    prefix: "$",
    suffix: "B",
    decimals: 1,
    caption: "MPC-secured across principal mandates.",
  },
  {
    icon: Network,
    label: "Chains supported",
    val: 14,
    caption: "L1s + L2s · unified reporting.",
  },
  {
    icon: Boxes,
    label: "MPC signers",
    val: 7,
    caption: "Threshold policy · geographic diversity.",
  },
  {
    icon: Activity,
    label: "Attestations",
    val: 4,
    caption: "SOC 2 · ISO 27001 · Chainalysis · Fireblocks.",
  },
];

const feed = [
  { t: "just now", tag: "TREASURY", txt: "Rebalance executed · USDC → T-Bills tokenized · 12.4M" },
  { t: "2 min", tag: "CUSTODY", txt: "Cold-vault → warm approval · 3-of-5 · 480 ETH" },
  { t: "9 min", tag: "AUDIT", txt: "Chainalysis screen · pass · 27 counterparties" },
  { t: "14 min", tag: "POLICY", txt: "Whitelist update signed · MENA Family Office" },
  { t: "31 min", tag: "REPORT", txt: "Weekly NAV published · principal + auditor cc'd" },
];

export default function OnChainProof() {
  return (
    <section
      id="on-chain"
      className="relative py-24 md:py-32 border-t border-[var(--line)] bg-ink"
      data-testid="onchain-proof-section"
    >
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-eyebrow uppercase text-emerald mb-4 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald animate-pulse" />
              Live · Digital Asset Infrastructure
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Institutional-grade, on-chain — and{" "}
              <em className="text-gold not-italic">provable</em>.
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 md:pt-6 text-ivory/70 leading-relaxed">
            Sample telemetry from an active MPC-custody mandate. Redacted for
            confidentiality. Your desk, your data, your audit trail.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5">
          {/* Metrics grid */}
          <div className="lg:col-span-7 grid grid-cols-2 gap-4 md:gap-5">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="relative border border-[var(--line)] bg-ink-2 p-6 md:p-8 min-h-[190px] flex flex-col justify-between hover:border-emerald/50 transition-colors"
                data-testid={`onchain-metric-${i}`}
              >
                <div className="flex items-center justify-between">
                  <s.icon size={20} strokeWidth={1.25} className="text-emerald" />
                  <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                    {s.label}
                  </span>
                </div>
                <div className="font-display text-5xl md:text-6xl text-ivory leading-none">
                  <Number
                    to={s.val}
                    prefix={s.prefix || ""}
                    suffix={s.suffix || ""}
                    decimals={s.decimals || 0}
                  />
                </div>
                <div className="text-xs text-ivory/60 leading-relaxed">
                  {s.caption}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Activity feed */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative border border-[var(--line)] bg-ink-2 p-6 md:p-8 flex flex-col"
            data-testid="onchain-feed"
          >
            <div className="flex items-center justify-between pb-4 border-b border-[var(--line)]">
              <div className="text-[10px] tracking-eyebrow uppercase text-emerald flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald animate-pulse" />
                Activity feed · redacted
              </div>
              <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                UTC
              </span>
            </div>
            <ul className="mt-4 divide-y divide-[var(--line)] flex-1">
              {feed.map((f, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                  className="py-4 grid grid-cols-12 gap-3 items-baseline"
                >
                  <span className="col-span-3 text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                    {f.t}
                  </span>
                  <span className="col-span-2 text-[10px] tracking-eyebrow uppercase text-gold">
                    {f.tag}
                  </span>
                  <span className="col-span-7 text-sm text-ivory/85 leading-snug">
                    {f.txt}
                  </span>
                </motion.li>
              ))}
            </ul>
            <div className="mt-4 pt-4 border-t border-[var(--line)] flex items-center justify-between">
              <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                Illustrative · sample data
              </span>
              <a
                href="#workshop"
                data-testid="onchain-cta"
                className="text-[10px] tracking-eyebrow uppercase text-gold gold-underline"
              >
                Request a live walkthrough →
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
